import { formatAmount } from "../../include/money";
import { useCallback, useContext, useEffect, useState } from "react";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { callPostApi } from "../../function/ApiCallFunction";
import { DataContext } from "../../context/DataContext";
import { canAdd, canEdit } from "../../include/staffAccess";
import { consoleColors, primaryButtonSx } from "../admin/consoleTheme";
import { hasEnded, indiaDate } from "../../include/indiaDate";
import { LaunchChooser, LaunchTimeFields, resizeLaunchTimes } from "../admin/LaunchTimes";

const emptyForm = {
  draw_id: "",
  title: "",
  frequency: "hour",
  start_at: "",
  end_at: "",
  min_sale_amount: "",
  print_no: "",
  background_image: "",
  back_image: "",
  banner: "",
};

function toLocal(value) {
  return String(value || "").replace(" ", "T").slice(0, 16);
}

function readImage(file) {
  return new Promise((resolve, reject) => {
    if (!file) return resolve("");
    if (file.size > 900000) return reject(new Error("Use an image under 900 KB"));
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read the image"));
    reader.readAsDataURL(file);
  });
}

export default function LuckyDrawMaster() {
  const navigate = useNavigate();
  const { loginData } = useContext(DataContext);
  const storeId = loginData?.store_info?.store_id;
  const [draws, setDraws] = useState([]);
  const [code, setCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [requests, setRequests] = useState([]);
  const [chooser, setChooser] = useState(null);

  const load = useCallback(async () => {
    if (!storeId) return;
    const result = await callPostApi("store-draw", { mod: "DRAW_LIST", data_arr: { store_id: storeId } });
    if (result.status === "200") setDraws(result.data?.draws || []);
  }, [storeId]);

  const loadRequests = useCallback(async () => {
    if (!storeId) return;
    const result = await callPostApi("store-draw", { mod: "CLUB_MY_REQUESTS", data_arr: { store_id: storeId } });
    if (result.status === "200") setRequests(result.data?.requests || []);
  }, [storeId]);

  useEffect(() => { load(); loadRequests(); }, [load, loadRequests]);

  const ownDraw = (draw) => draw.scope !== "club" && draw.store_id === storeId;

  const join = async () => {
    setJoining(true);
    try {
      const result = await callPostApi("store-draw", { mod: "CLUB_JOIN", data_arr: { store_id: storeId, code } });
      if (result.status === "200") {
        toast.success(result.data?.message || "Request sent");
        setCode("");
        loadRequests();
      } else toast.info(result.status_message || result.data?.error || "Could not send the request");
    } finally {
      setJoining(false);
    }
  };

  const toggle = async (draw) => {
    const result = await callPostApi("store-draw", {
      mod: "DRAW_STATUS",
      data_arr: { store_id: storeId, draw_id: draw.draw_id, status: draw.status === "1" ? "0" : "1" },
    });
    if (result.status === "200") {
      toast.success(result.data?.message);
      load();
    } else toast.info(result.status_message || result.data?.error || "Could not update");
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 2, mb: 2 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Join Community Lucky Draw Coupons</Typography>
        <Typography sx={{ fontSize: 13, color: consoleColors.muted, mt: 0.4, mb: 1.5 }}>Enter the club code from the console. The request waits until it is approved.</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <TextField size="small" label="Club code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} sx={{ minWidth: 180 }} />
          <Button variant="contained" sx={primaryButtonSx} disabled={joining || !code.trim()} onClick={join}>{joining ? "Sending..." : "Send request"}</Button>
        </Box>
        {requests.map((item) => (
          <Typography key={item.request_id} sx={{ fontSize: 13, color: consoleColors.ink, mt: 1 }}>
            {item.title} · {item.code} · {item.status} · {indiaDate(item.date_added)}
          </Typography>
        ))}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
        <Typography sx={{ color: consoleColors.muted, fontSize: 14 }}>Generate Lucky Draw Coupons for your store</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="outlined" sx={{ textTransform: "none" }} onClick={() => navigate("/registerSale")}>Register sale</Button>
          {canAdd(loginData) && <Button variant="contained" sx={primaryButtonSx} onClick={() => navigate("/luckydraws/new")}><Add sx={{ mr: 0.5 }} /> Create Lucky Draw Coupons</Button>}
        </Box>
      </Box>

      {draws.length === 0 && (
        <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, p: 4, textAlign: "center", color: consoleColors.muted }}>
          No Lucky Draw Coupons for this store yet.
        </Box>
      )}

      <Box sx={{ display: "grid", gap: 1.5 }}>
        {draws.map((draw) => {
          const ended = Boolean(draw.ended) || hasEnded(draw.end_at);
          const live = !ended && draw.status === "1";
          return (
          <Box key={draw.draw_id} sx={{ background: live ? "#fff" : "#f6eaea", border: `1px solid ${live ? consoleColors.line : "#e7cfcf"}`, borderRadius: 2.5, p: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <QRCodeCanvas value={draw.draw_id} size={72} />
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: consoleColors.ink }}>{draw.title}</Typography>
              <Typography sx={{ fontSize: 13, color: consoleColors.muted, mt: 0.4 }}>
                {draw.scope === "club" ? `Club · ${draw.club_title}` : "This store"} · {draw.frequency_label} · {indiaDate(draw.start_at)} to {indiaDate(draw.end_at)}
              </Typography>
              <Typography sx={{ fontSize: 13, color: consoleColors.ink, mt: 0.4 }}>
                Draw {draw.print_no ? Number(draw.print_no) : "-"} · Minimum sale ₹{formatAmount(draw.min_sale_amount)} · {draw.issued_count || 0} issued · {draw.winner_count || 0} winners · {ended ? "Ended" : live ? "Enabled" : "Disabled"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button size="small" variant="contained" sx={primaryButtonSx} disabled={!ownDraw(draw)} onClick={() => setChooser(draw)}>Launch draw</Button>
              <Button size="small" sx={{ textTransform: "none" }} disabled={!ownDraw(draw) || !canEdit(loginData)} onClick={() => navigate(`/luckydraws/${draw.draw_id}/edit`)}>Edit</Button>
              <Button size="small" sx={{ textTransform: "none" }} disabled={!ownDraw(draw) || ended} onClick={() => toggle(draw)}>{live ? "Disable" : "Enable"}</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/luckydrawcoupon?draw_id=${draw.draw_id}`)}>Print</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/luckydraw/${draw.draw_id}?view=result`)}>Result</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/luckydraw/${draw.draw_id}?view=issued`)}>Issued</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/luckydraw/${draw.draw_id}?view=winners`)}>Winners</Button>
            </Box>
          </Box>
          );
        })}
      </Box>
      <LaunchChooser
        open={Boolean(chooser)}
        title={chooser?.title || ""}
        frequencyLabel={chooser?.frequency_label}
        launches={chooser?.launches || []}
        loadSlots={(resultMode) => callPostApi("store-draw", { mod: "DRAW_SLOTS", data_arr: { store_id: storeId, draw_id: chooser.draw_id, result_mode: resultMode } })}
        onClose={() => setChooser(null)}
        onOpen={(slot) => navigate(`/luckydraws/${chooser.draw_id}/launch?slot=${slot.index}`)}
        onOpenFrequency={(resultMode, slot) => navigate(`/luckydraws/${chooser.draw_id}/launch?mode=${resultMode}&key=${encodeURIComponent(slot.key)}`)}
        onShare={async (payload) => {
          const result = await callPostApi("store-draw", { mod: "LAUNCH_LINK", data_arr: { store_id: storeId, kind: "coupon", draw_id: chooser.draw_id, ...payload } });
          if (result.status !== "200") {
            toast.info(result.status_message || result.data?.error || "Could not make the launch link");
            return "";
          }
          return `${window.location.origin}${result.data.path}`;
        }}
      />
    </Box>
  );
}

export function StoreDrawForm() {
  const { drawId } = useParams();
  const navigate = useNavigate();
  const { loginData } = useContext(DataContext);
  const storeId = loginData?.store_info?.store_id;
  const [form, setForm] = useState({ ...emptyForm, launch_count: "1", launch_times: [""], launch_locked: [] });
  const [saving, setSaving] = useState(false);
  const [clubDraw, setClubDraw] = useState(false);

  useEffect(() => {
    if (!drawId || !storeId) return;
    let mounted = true;
    callPostApi("store-draw", { mod: "DRAW_DETAIL", data_arr: { store_id: storeId, draw_id: drawId } }).then((result) => {
      if (!mounted || result.status !== "200") return;
      const full = result.data?.draw || {};
      setClubDraw(full.scope === "club" || (full.store_id && full.store_id !== storeId));
      setForm({
        draw_id: full.draw_id || drawId,
        title: full.title || "",
        frequency: full.frequency || "hour",
        start_at: toLocal(full.start_at),
        end_at: toLocal(full.end_at),
        min_sale_amount: full.min_sale_amount ?? "",
        print_no: full.print_no || "",
        background_image: full.background_image || "",
        back_image: full.back_image || "",
        banner: full.banner || "",
        launch_count: String(Math.max(1, (full.launches || []).length || 1)),
        launch_times: resizeLaunchTimes((full.launches || []).map((slot) => toLocal(slot.at)), Math.max(1, (full.launches || []).length || 1)),
        launch_locked: (full.launches || []).map((slot) => slot.launched === "1"),
      });
    });
    return () => { mounted = false; };
  }, [drawId, storeId]);

  const onFile = async (key, file) => {
    try {
      const image = await readImage(file);
      setForm((prev) => ({ ...prev, [key]: image }));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const save = async () => {
    if (clubDraw) return;
    setSaving(true);
    try {
      const result = await callPostApi("store-draw", {
        mod: "DRAW_SAVE",
        data_arr: { ...form, store_id: storeId, scope: "store", min_sale_amount: Number(form.min_sale_amount || 0), launch_times: form.launch_times },
      });
      if (result.status === "200") {
        toast.success(result.data?.message || "Saved");
        navigate("/luckydraws");
      } else toast.info(result.status_message || result.data?.error || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 760 }}>
      <Button sx={{ textTransform: "none", mb: 2 }} onClick={() => navigate("/luckydraws")}>Back to Lucky Draw Coupons</Button>
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 }, display: "grid", gap: 1.5 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 750 }}>{drawId ? "Edit Lucky Draw Coupons" : "Create Lucky Draw Coupons"}</Typography>
        {clubDraw && <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>Club Lucky Draw Coupons are managed from the console. Edit and disable stay off for this store.</Typography>}
        <TextField label="Title" value={form.title} disabled={clubDraw} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <TextField select label="Lucky Draw Frequency" value={form.frequency} disabled={clubDraw} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
          <MenuItem value="hour">Every 1 hour</MenuItem>
          <MenuItem value="day">Every day</MenuItem>
          <MenuItem value="all">All entries</MenuItem>
        </TextField>
        <TextField label="Starts" type="datetime-local" value={form.start_at} disabled={clubDraw} onChange={(e) => setForm({ ...form, start_at: e.target.value })} InputLabelProps={{ shrink: true }} />
        <TextField label="Ends" type="datetime-local" value={form.end_at} disabled={clubDraw} onChange={(e) => setForm({ ...form, end_at: e.target.value })} InputLabelProps={{ shrink: true }} />
        <TextField label="Minimum sale amount" type="number" value={form.min_sale_amount} disabled={clubDraw} onChange={(e) => setForm({ ...form, min_sale_amount: e.target.value })} helperText="A coupon is issued when the sale is at least this amount." />
        <TextField label="Draw number" value={form.print_no} disabled={clubDraw} onChange={(e) => setForm({ ...form, print_no: e.target.value.replace(/\D/g, "").slice(0, 2) })} helperText="1, 2, 3… printed in the coupon barcode. Leave blank to assign the next number." inputProps={{ inputMode: "numeric", maxLength: 2 }} />
        {!clubDraw && (
          <LaunchTimeFields
            count={form.launch_count}
            times={form.launch_times}
            locked={form.launch_locked}
            onCount={(value) => {
              const count = Math.min(30, Math.max(1, Number(value) || 1));
              const kept = (form.launch_locked || []).filter(Boolean).length;
              if (count < kept) return;
              setForm({ ...form, launch_count: String(count), launch_times: resizeLaunchTimes(form.launch_times, count) });
            }}
            onTime={(index, value) => {
              const launch_times = form.launch_times.slice();
              launch_times[index] = value;
              setForm({ ...form, launch_times });
            }}
          />
        )}
        <Button component="label" disabled={clubDraw} sx={{ textTransform: "none", justifyContent: "flex-start" }}>
          Front image {form.background_image ? "· selected" : ""}
          <input hidden type="file" accept="image/*" onChange={(e) => onFile("background_image", e.target.files?.[0])} />
        </Button>
        {form.background_image && <Box component="img" src={form.background_image} alt="Front" sx={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 1 }} />}
        <Button component="label" disabled={clubDraw} sx={{ textTransform: "none", justifyContent: "flex-start" }}>
          Back image (optional) {form.back_image ? "· selected" : ""}
          <input hidden type="file" accept="image/*" onChange={(e) => onFile("back_image", e.target.files?.[0])} />
        </Button>
        {form.back_image && <Box component="img" src={form.back_image} alt="Back" sx={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 1 }} />}
        <Button component="label" disabled={clubDraw} sx={{ textTransform: "none", justifyContent: "flex-start" }}>
          Banner {form.banner ? "· selected" : ""}
          <input hidden type="file" accept="image/*" onChange={(e) => onFile("banner", e.target.files?.[0])} />
        </Button>
        {form.banner && <Box component="img" src={form.banner} alt="Banner" sx={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 1 }} />}
        {form.draw_id && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <QRCodeCanvas value={form.draw_id} size={84} />
            <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>QR for this draw. Code {form.draw_id}</Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button onClick={() => navigate("/luckydraws")} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" sx={primaryButtonSx} disabled={saving || clubDraw} onClick={save}>{saving ? "Saving..." : "Save"}</Button>
        </Box>
      </Box>
    </Box>
  );
}
